import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  CircularProgress,
  IconButton,
  InputBase,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Checkbox,
  Tooltip,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/system";
import { THEME_COLORS } from "../../context/constants";
import { Asset } from "../../context/types";
import {
  getAssetsByOrganization,
  getAssetsByWorkspace,
  deleteAsset,
  uploadBackgroundAssetV2,
} from "../../client/api";
import { useUserContext } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";

// Styled components
const SearchBar = styled(Box)({
  display: "flex",
  alignItems: "center",
  backgroundColor: "#f1f1f1",
  border: `1px solid ${THEME_COLORS.gray}`,
  padding: "4px 12px",
  borderRadius: "14px",
  maxWidth: "300px",
});

const AssetCard = styled(Box)<{ selected?: boolean }>(({ selected }) => ({
  position: "relative",
  borderRadius: "8px",
  overflow: "hidden",
  cursor: "pointer",
  border: selected ? `3px solid ${THEME_COLORS.yellow}` : "3px solid transparent",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

const AssetImage = styled("img")({
  width: "100%",
  height: "150px",
  objectFit: "cover",
  display: "block",
});

const AssetVideo = styled("video")({
  width: "100%",
  height: "150px",
  objectFit: "cover",
  display: "block",
});

const AssetOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.5) 100%)",
  opacity: 0,
  transition: "opacity 0.2s ease",
  display: "flex",
  alignItems: "flex-end",
  padding: "8px",
  pointerEvents: "none", // Don't block clicks on buttons
  zIndex: 1,
});

const CheckOverlay = styled(Box)({
  position: "absolute",
  top: "8px",
  right: "8px",
  zIndex: 2,
});

const DeleteButton = styled(IconButton)({
  position: "absolute",
  top: "8px",
  left: "8px",
  zIndex: 3,
  backgroundColor: "rgba(255,255,255,0.9)",
  "&:hover": {
    backgroundColor: "rgba(255,0,0,0.8)",
    color: "white",
  },
});

const TypeBadge = styled(Chip)({
  position: "absolute",
  bottom: "8px",
  left: "8px",
  fontSize: "10px",
  height: "20px",
});

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

interface AssetLibraryModalProps {
  open: boolean;
  onClose: () => void;
  mode: "manage" | "select";
  onSelect?: (asset: Asset) => void;
  filterType?: "image" | "video" | "all";
  title?: string;
}

type FilterOption = "all" | "images" | "videos" | "my-uploads";
type WorkspaceFilter = "organization" | string; // "organization" or workspace ID

const AssetLibraryModal: React.FC<AssetLibraryModalProps> = ({
  open,
  onClose,
  mode,
  onSelect,
  filterType = "all",
  title,
}) => {
  const { user, currentWorkspace } = useUserContext();
  const { addNotification } = useNotification();

  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<FilterOption>("all");
  const [workspaceFilter, setWorkspaceFilter] = useState<WorkspaceFilter>("organization");
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Fetch assets
  const fetchAssets = async () => {
    if (!user?.organization?._id) return;

    setLoading(true);
    try {
      let fetchedAssets: Asset[];

      if (workspaceFilter === "organization") {
        fetchedAssets = await getAssetsByOrganization(user.organization._id);
      } else {
        fetchedAssets = await getAssetsByWorkspace(workspaceFilter);
      }

      setAssets(fetchedAssets);
    } catch (error) {
      console.error("Error fetching assets:", error);
      addNotification("Failed to load assets", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAssets();
      setSelectedAsset(null);
    }
  }, [open, workspaceFilter]);

  // Filter assets based on search and type
  const filteredAssets = useMemo(() => {
    let result = assets;

    // Apply type filter from props (for select mode)
    if (filterType !== "all") {
      result = result.filter((asset) => asset.type === filterType);
    }

    // Apply type filter from dropdown
    if (typeFilter === "images") {
      result = result.filter((asset) => asset.type === "image");
    } else if (typeFilter === "videos") {
      result = result.filter((asset) => asset.type === "video");
    } else if (typeFilter === "my-uploads") {
      const userId = typeof user?._id === "string" ? user._id : user?._id;
      result = result.filter((asset) => {
        const assetUserId = typeof asset.userId === "string" ? asset.userId : (asset.userId as any)?._id;
        return assetUserId === userId;
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (asset) =>
          asset.name?.toLowerCase().includes(query) ||
          asset.type.toLowerCase().includes(query)
      );
    }

    return result;
  }, [assets, filterType, typeFilter, searchQuery, user?._id]);

  const handleAssetClick = (asset: Asset) => {
    if (mode === "select") {
      setSelectedAsset(asset);
    }
  };

  const handleDelete = async (assetId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!window.confirm("Are you sure you want to delete this asset?")) {
      return;
    }

    setDeleting(assetId);
    try {
      await deleteAsset(assetId);
      setAssets((prev) => prev.filter((a) => a._id !== assetId));
      addNotification("Asset deleted successfully", "success");

      if (selectedAsset?._id === assetId) {
        setSelectedAsset(null);
      }
    } catch (error) {
      console.error("Error deleting asset:", error);
      addNotification("Failed to delete asset", "error");
    } finally {
      setDeleting(null);
    }
  };

  const handleConfirmSelection = () => {
    if (selectedAsset && onSelect) {
      onSelect(selectedAsset);
      onClose();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (!user?._id || !currentWorkspace?._id) {
      addNotification("Unable to upload: missing user or workspace information", "error");
      return;
    }

    setUploading(true);
    const uploadedAssets: Asset[] = [];
    const failedUploads: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file type
      const validTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "video/mp4",
        "video/quicktime",
      ];

      if (!validTypes.includes(file.type)) {
        failedUploads.push(`${file.name} (unsupported type)`);
        continue;
      }

      // Validate file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        failedUploads.push(`${file.name} (exceeds 50MB limit)`);
        continue;
      }

      try {
        const newAsset = await uploadBackgroundAssetV2(file, user._id, currentWorkspace._id, {
          organizationId: user.organization?._id,
          source: "heygen", // Default to heygen as requested
        });
        uploadedAssets.push(newAsset);
      } catch (error: any) {
        console.error(`Error uploading ${file.name}:`, error);
        failedUploads.push(`${file.name} (${error.message || "upload failed"})`);
      }
    }

    // Update assets list with newly uploaded assets
    if (uploadedAssets.length > 0) {
      setAssets((prev) => [...uploadedAssets, ...prev]);
      addNotification(
        `Successfully uploaded ${uploadedAssets.length} asset${uploadedAssets.length > 1 ? "s" : ""}`,
        "success"
      );
    }

    if (failedUploads.length > 0) {
      addNotification(`Failed to upload: ${failedUploads.join(", ")}`, "error");
    }

    // Reset the input
    event.target.value = "";
    setUploading(false);
  };

  const getModalTitle = () => {
    if (title) return title;
    return mode === "manage" ? "Asset Library" : "Select Asset";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: "70vh", maxHeight: "90vh" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${THEME_COLORS.gray}`,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {getModalTitle()}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Filters Row */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            mt: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Search */}
          <SearchBar>
            <SearchIcon sx={{ color: THEME_COLORS.darkGray, mr: 1 }} />
            <InputBase
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flex: 1, fontSize: "14px" }}
            />
          </SearchBar>

          {/* Type Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ color: THEME_COLORS.darkGray }}>Filter by</InputLabel>
            <Select
              value={typeFilter}
              label="Filter by"
              onChange={(e) => setTypeFilter(e.target.value as FilterOption)}
            >
              <MenuItem value="all">All Assets</MenuItem>
              <MenuItem value="images">Images Only</MenuItem>
              <MenuItem value="videos">Videos Only</MenuItem>
              <MenuItem value="my-uploads">My Uploads</MenuItem>
            </Select>
          </FormControl>

          {/* Workspace Filter */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ color: THEME_COLORS.darkGray }}>Scope</InputLabel>
            <Select
              value={workspaceFilter}
              label="Scope"
              onChange={(e) => setWorkspaceFilter(e.target.value as WorkspaceFilter)}
            >
              <MenuItem value="organization">All Organization</MenuItem>
              {currentWorkspace && (
                <MenuItem value={currentWorkspace._id}>
                  Current Workspace
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Upload Button */}
          <Button
            component="label"
            variant="contained"
            startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
            disabled={uploading}
            sx={{
              backgroundColor: THEME_COLORS.yellow,
              color: "black",
              "&:hover": {
                backgroundColor: THEME_COLORS.darkYellow,
              },
              "&:disabled": {
                backgroundColor: THEME_COLORS.gray,
              },
            }}
          >
            {uploading ? "Uploading..." : "Upload Assets"}
            <VisuallyHiddenInput
              type="file"
              accept="image/jpeg,image/png,image/gif,video/mp4,video/quicktime"
              multiple
              onChange={handleFileUpload}
              disabled={uploading}
            />
          </Button>

          {/* Asset Count */}
          <Typography variant="body2" color="textSecondary" sx={{ ml: "auto" }}>
            {filteredAssets.length} asset{filteredAssets.length !== 1 ? "s" : ""}
          </Typography>
        </Box>

        {/* Assets Grid */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <CircularProgress sx={{ color: THEME_COLORS.yellow }} />
          </Box>
        ) : filteredAssets.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
              color: THEME_COLORS.darkGray,
            }}
          >
            <ImageIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6">No assets found</Typography>
            <Typography variant="body2">
              {searchQuery
                ? "Try adjusting your search or filters"
                : "Upload some assets to get started"}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredAssets.map((asset) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={asset._id}>
                <AssetCard
                  selected={mode === "select" && selectedAsset?._id === asset._id}
                  onClick={() => handleAssetClick(asset)}
                >
                  {/* Selection Checkbox (select mode only) */}
                  {mode === "select" && (
                    <CheckOverlay>
                      {selectedAsset?._id === asset._id ? (
                        <CheckCircleIcon
                          sx={{ color: THEME_COLORS.yellow, fontSize: 28 }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            border: "2px solid white",
                            backgroundColor: "rgba(0,0,0,0.3)",
                          }}
                        />
                      )}
                    </CheckOverlay>
                  )}

                  {/* Delete Button */}
                  <Tooltip title="Delete asset">
                    <DeleteButton
                      size="small"
                      onClick={(e) => handleDelete(asset._id, e)}
                      disabled={deleting === asset._id}
                    >
                      {deleting === asset._id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </DeleteButton>
                  </Tooltip>

                  {/* Asset Preview */}
                  {asset.type === "image" ? (
                    <AssetImage src={asset.url} alt={asset.name || "Asset"} />
                  ) : (
                    <AssetVideo src={asset.url} muted />
                  )}

                  {/* Type Badge */}
                  <TypeBadge
                    label={asset.type}
                    size="small"
                    icon={
                      asset.type === "image" ? (
                        <ImageIcon sx={{ fontSize: 12 }} />
                      ) : (
                        <VideoLibraryIcon sx={{ fontSize: 12 }} />
                      )
                    }
                    sx={{
                      backgroundColor:
                        asset.type === "image"
                          ? "rgba(76, 175, 80, 0.9)"
                          : "rgba(33, 150, 243, 0.9)",
                      color: "white",
                    }}
                  />

                  {/* Asset Name Overlay */}
                  <AssetOverlay className="asset-overlay">
                    <Typography
                      variant="caption"
                      sx={{
                        color: "white",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%",
                      }}
                    >
                      {asset.name || "Unnamed"}
                    </Typography>
                  </AssetOverlay>
                </AssetCard>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${THEME_COLORS.gray}`,
          px: 3,
          py: 2,
        }}
      >
        <Button onClick={onClose} sx={{ color: THEME_COLORS.darkGray }}>
          {mode === "manage" ? "Close" : "Cancel"}
        </Button>

        {mode === "select" && (
          <Button
            variant="contained"
            onClick={handleConfirmSelection}
            disabled={!selectedAsset}
            sx={{
              backgroundColor: THEME_COLORS.yellow,
              color: "black",
              "&:hover": {
                backgroundColor: THEME_COLORS.darkYellow,
              },
              "&:disabled": {
                backgroundColor: THEME_COLORS.gray,
              },
            }}
          >
            Select Asset
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AssetLibraryModal;
